import React from "react";
import { FaBolt, FaRocket, FaPalette } from "react-icons/fa";

const CreativeVibrantQuotation = ({ data }) => {
    const {
        to,
        clientAddress,
        clientPhone,
        clientEmail,
        title,
        date,
        expiryDate,
        items,
        subTotal,
        taxRate,
        taxAmount,
        discount,
        totalAmount,
        notes,
        terms,
        companyDetails
    } = data;

    return (
        <div className="w-full bg-white p-12 font-sans tracking-tight text-slate-900 min-h-[1100px] flex flex-col relative overflow-hidden">
            {/* Background Decorations */}
            <div className="absolute top-0 right-0 w-[50%] h-[30%] bg-gradient-to-br from-slate-100 to-transparent -rotate-12 translate-x-20 -translate-y-20 rounded-[5rem]"></div>
            <div className="absolute bottom-0 left-0 w-[40%] h-[20%] bg-gradient-to-tr from-slate-50 to-transparent rotate-12 -translate-x-20 translate-y-20 rounded-[5rem]"></div>

            <div className="relative z-10 flex-1 flex flex-col justify-between">
                <div>
                    {/* Header */}
                    <div className="flex justify-between items-start mb-20">
                        <div className="space-y-6">
                            <div className="flex items-center gap-4">
                                <div className="w-14 h-14 bg-slate-900 rounded-2xl flex items-center justify-center text-white text-2xl shadow-2xl">
                                    <FaRocket className="opacity-50" />
                                </div>
                                <div className="text-3xl font-black uppercase tracking-tighter text-slate-900 italic">
                                    {companyDetails?.name}
                                </div>
                            </div>
                            <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] flex items-center gap-2 italic border-l-2 border-slate-900 pl-4">
                                {companyDetails?.address}
                            </div>
                        </div>

                        <div className="text-right space-y-2">
                            <div className="text-xs font-black uppercase tracking-[0.5em] text-slate-300 mb-4 italic">Project Proposal</div>
                            <h1 className="text-9xl font-black text-slate-900 tracking-tighter leading-none italic uppercase -mr-8 select-none">RECORD</h1>
                            <div className="text-xl font-black text-slate-900 uppercase italic tracking-tighter mt-4"># {title}</div>
                        </div>
                    </div>

                    {/* Meta Info */}
                    <div className="grid grid-cols-2 gap-12 mb-20">
                        <div className="bg-slate-900 rounded-[4rem] p-12 text-white flex justify-between items-end shadow-2xl border border-slate-800">
                            <div className="space-y-4">
                                <div className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-500 italic">Proposed Recipient</div>
                                <h2 className="text-4xl font-black italic tracking-tighter underline underline-offset-[12px] decoration-slate-700 decoration-4">{to}</h2>
                                <p className="text-xs font-bold text-slate-400 max-w-[240px] italic pt-8 leading-relaxed">{clientAddress}</p>
                            </div>
                            <div className="text-right">
                                <div className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-500 mb-2 italic">Term Validity</div>
                                <div className="text-xl font-black italic text-white">{expiryDate}</div>
                            </div>
                        </div>

                        <div className="flex-1 bg-white rounded-l-[4rem] p-16 flex flex-col justify-between shadow-[-50px_0_100px_rgba(0,0,0,0.3)] border-l border-white/10">
                            <div>
                                <div className="flex justify-between items-start mb-24">
                                    <div className="space-y-2 group">
                                        <span className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-300 border-l-4 border-slate-900 pl-4 py-1">Proposed To</span>
                                        <h2 className="text-5xl font-black text-slate-900 tracking-tighter uppercase italic leading-none pt-4">{to}</h2>
                                        <p className="text-xs font-bold text-slate-400 max-w-[240px] italic leading-relaxed pt-2">{clientAddress}</p>
                                    </div>
                                    <div className="text-right space-y-2 pt-2">
                                        <h1 className="text-8xl font-black text-slate-50 uppercase leading-none mb-6 -mr-16 tracking-tighter italic select-none">ESTIMATE</h1>
                                        <div className="text-lg font-black text-slate-900 uppercase italic tracking-tighter"># {title}</div>
                                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.4em]">Expires: {expiryDate}</div>
                                    </div>
                                </div>

                                <div className="mb-16">
                                    <table className="w-full text-left">
                                        <thead className="border-b-4 border-slate-900">
                                            <tr className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-300">
                                                <th className="pb-6 pl-2">Service / Component</th>
                                                <th className="pb-6 text-center">Qty</th>
                                                <th className="pb-6 text-right">Rate</th>
                                                <th className="pb-6 text-right pr-2">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 border-b border-slate-100">
                                            {(items || []).map((item, idx) => (
                                                <tr key={idx} className="group hover:bg-slate-50 transition-all duration-300">
                                                    <td className="py-10 pr-10 pl-2">
                                                        <div className="text-xl font-black text-slate-900 uppercase italic tracking-tighter transition-transform group-hover:translate-x-2 duration-300">{item.item}</div>
                                                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Official Protocol</div>
                                                    </td>
                                                    <td className="py-10 text-center text-sm font-black text-slate-300 italic leading-none">{item.qty}</td>
                                                    <td className="py-10 text-right text-sm font-black text-slate-900 uppercase leading-none tracking-tighter italic">Nu. {parseFloat(item.unitPrice).toLocaleString()}</td>
                                                    <td className="py-10 text-right text-2xl font-black text-slate-900 uppercase leading-none tracking-tighter italic pr-2">Nu. {parseFloat(item.amount).toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="space-y-12">
                                <div className="flex justify-between items-start pt-12 border-t border-slate-100">
                                    <div className="max-w-xs space-y-6">
                                        <div>
                                            <h4 className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-300 mb-4 italic">Proposal Remarks</h4>
                                            <p className="text-xs font-bold text-slate-500 leading-relaxed italic border-l-2 border-slate-100 pl-6">
                                                {notes || "A formal estimate prepared for strategic project analysis. Prices are valid until the specified expiry date."}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-4 text-[9px] font-black uppercase tracking-widest text-slate-300 italic opacity-50">
                                            Official Digital Quotation Record
                                        </div>
                                    </div>
                                    <div className="w-80 space-y-6">
                                        <div className="space-y-4">
                                            <div className="flex justify-between text-xs font-black text-slate-400 uppercase tracking-[0.2em]">
                                                <span>Base Subtotal</span>
                                                <span className="text-slate-900">Nu. {parseFloat(subTotal).toLocaleString()}</span>
                                            </div>
                                            <div className="flex justify-between text-xs font-black text-slate-400 uppercase tracking-[0.2em]">
                                                <span>Tax Component ({taxRate}%)</span>
                                                <span className="text-slate-900">Nu. {parseFloat(taxAmount).toLocaleString()}</span>
                                            </div>
                                            {discount > 0 && (
                                                <div className="flex justify-between text-xs font-black text-slate-400 uppercase tracking-[0.2em]">
                                                    <span>Discount Offset</span>
                                                    <span className="text-slate-900">- Nu. {parseFloat(discount).toLocaleString()}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="pt-10 border-t-4 border-slate-900 flex justify-between items-end">
                                            <span className="text-xs font-black uppercase tracking-[0.5em] text-slate-900">Final Estimate</span>
                                            <div className="text-5xl font-black text-slate-900 tracking-tighter italic leading-none">Nu. {parseFloat(totalAmount).toLocaleString()}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-between items-end text-[10px] font-black uppercase tracking-[0.6em] text-slate-200 italic border-t border-slate-50 pt-10">
                        <div>Official Strategic Proposal Document</div>
                        <div className="text-slate-900 italic font-black">NexrBook Professional</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreativeVibrantQuotation;
